import { ModalBottomButtons } from '@bp/ui-components';
import { FileListUploader } from 'components/Files/components/FileList/FileListUploader';
import { BpFile } from 'components/Files/hooks/useFiles';
import { TipTapEditor } from 'components/TipTap/TipTapEditor';
import { Form, Formik, FormikHelpers } from 'formik';
import { useActivityProtocol } from 'hooks/matrix/useActivityProtocol';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { fileEntriesConnect } from 'utils/connectLib';
import { NotificationReason } from 'utils/matrixClient';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { UpdateSubmissionsMutation, useSubmissions } from '../hooks/useSubmissions';

type SubmissionFeedbackFormType = {
  text: string | null;
  files: BpFile[];
};

type SubmissionFeedbackFormProps = {
  submissionUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const SubmissionFeedbackForm = ({ submissionUuid, onLoad, onClose }: SubmissionFeedbackFormProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const [, updateSubmission] = useMutation(UpdateSubmissionsMutation);

  const { sendMessage } = useActivityProtocol();

  const { submissions, refetch } = useSubmissions({ courseUuid, assignmentUuid, submissionUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const feedback = submission.feedbackMaterial;

  const initialValues: SubmissionFeedbackFormType = {
    text: feedback?.text ?? null,
    files: feedback?.fileEntries ?? [],
  };

  async function handleSubmit(values: SubmissionFeedbackFormType, helpers: FormikHelpers<SubmissionFeedbackFormType>) {
    onLoad(true);

    const { error } = await updateSubmission({
      where: { uuid: submissionUuid },
      update: {
        feedbackMaterial: {
          delete: {},
          create: {
            node: {
              text: values.text,
              textMediaType: 'text/plain',
              fileEntries: fileEntriesConnect(values.files),
            },
          },
        },
      },
    });

    await sendMessage(submissionUuid, NotificationReason.SubmissionResponse);

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
      refetch();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  }

  return (
    <Formik<SubmissionFeedbackFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, dirty }) => (
        <Form>
          <TipTapEditor
            name='text'
            defaultValue={values.text}
            onChange={async (text) => {
              await setFieldValue('text', text);
            }}
            isDense
            className='mb-7'
          />
          <FileListUploader
            files={values.files}
            onFileUpload={async (file) => {
              await setFieldValue('files', [...values.files, file]);
            }}
            onFileRename={async (file) => {
              await setFieldValue(
                'files',
                values.files.map((f) => (f.uuid === file.uuid ? file : f)),
              );
            }}
            onFilesDelete={async (uuids) => {
              await setFieldValue(
                'files',
                values.files.filter((f) => !uuids.includes(f.uuid)),
              );
            }}
          />
          <ModalBottomButtons
            closeButton={{ callback: () => onClose() }}
            submitButton={{ text: t('common.send'), disabled: !dirty }}
          />
        </Form>
      )}
    </Formik>
  );
};
